/*
 * Yomiri Plugin Network Dispatcher
 * Dispatches protocol queries to plugin servers (HTTP) or reads from static plugin payloads.
 * Respects CORS standards and leverages ExtensionBridgeClient when available, with 0 bridge changes.
 */

import { ExtensionBridgeClient } from '../bridge/ExtensionBridgeClient.ts';
import type {
  InstalledPlugin,
  PluginCatalogResponse,
  PluginMetaResponse,
  PluginStreamResponse,
  PluginReaderResponse,
  YomiriPluginManifest,
} from './PluginTypes.ts';

export class PluginDispatcher {
  /**
   * Fetches and parses a remote plugin manifest.
   */
  public static async fetchManifest(manifestUrl: string): Promise<YomiriPluginManifest> {
    const url = manifestUrl.trim();
    const data = await this.requestJson(url);
    return data as YomiriPluginManifest;
  }

  /**
   * Fetches items for a specific catalog from a plugin.
   * Path: /catalog/{type}/{id}.json (or with query parameters: ?search=...&genre=...&skip=...)
   */
  public static async fetchCatalog(
    plugin: InstalledPlugin,
    type: string,
    catalogId: string,
    params?: { search?: string; genre?: string; skip?: number }
  ): Promise<PluginCatalogResponse> {
    // 1. Static plugin check
    if (plugin.manifest.behaviorHints?.staticData && plugin.staticPayload?.catalogs) {
      const key = `${type}:${catalogId}`;
      const items = plugin.staticPayload.catalogs[key] || plugin.staticPayload.catalogs[catalogId] || [];
      let filtered = items;
      if (params?.search) {
        const q = params.search.toLowerCase();
        filtered = filtered.filter(i => i.name.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q));
      }
      if (params?.genre) {
        filtered = filtered.filter(i => i.genres?.includes(params.genre!));
      }
      return { metas: filtered };
    }

    // Respect required catalog extras before issuing an invalid addon request.
    const catalog=plugin.manifest.catalogs.find(c=>c.id===catalogId&&c.type===type);
    for(const extra of catalog?.extra||[]){if(extra.isRequired&&!params?.[extra.name as keyof typeof params])throw new Error(`Este catálogo requiere el filtro ${extra.name}.`);}
    // 2. HTTP Plugin query
    const baseUrl = this.getPluginBaseUrl(plugin.url);
    const queryParts: string[] = [];
    if (params?.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params?.genre) queryParts.push(`genre=${encodeURIComponent(params.genre)}`);
    if (typeof params?.skip === 'number') queryParts.push(`skip=${params.skip}`);
    const qs = queryParts.length > 0 ? `/${queryParts.join('&')}` : '';

    const endpoint = `${baseUrl}/catalog/${encodeURIComponent(type)}/${encodeURIComponent(catalogId)}${qs}.json`;
    const res = await this.requestJson(endpoint);
    return (res as PluginCatalogResponse) || { metas: [] };
  }

  /**
   * Fetches metadata for a single item (episodes, seasons, synopsis).
   * Path: /meta/{type}/{id}.json
   */
  public static async fetchMeta(
    plugin: InstalledPlugin,
    type: string,
    id: string
  ): Promise<PluginMetaResponse | null> {
    // 1. Static plugin check
    if (plugin.manifest.behaviorHints?.staticData && plugin.staticPayload?.metas) {
      const meta = plugin.staticPayload.metas[id];
      if (meta) return { meta };
    }

    // 2. HTTP Plugin query
    const baseUrl = this.getPluginBaseUrl(plugin.url);
    const endpoint = `${baseUrl}/meta/${encodeURIComponent(type)}/${encodeURIComponent(id)}.json`;
    try {
      const res = await this.requestJson(endpoint);
      return res as PluginMetaResponse;
    } catch (err) {
      console.warn(`[PluginDispatcher] Failed to fetch meta for ${id} from ${plugin.manifest.name}:`, err);
      return null;
    }
  }

  /**
   * Classifies a stream item to ensure honest UX:
   * P2P / Torrents, magnet links, and non-web-ready streams are clearly declared incompatible.
   */
  public static classifyStream(stream: any): PluginStreamItem {
    const title = stream.title || stream.name || 'Reproducir';
    const isTorrent = Boolean(stream.infoHash || (typeof stream.url === 'string' && stream.url.startsWith('magnet:')));
    const isDirectPlayable = Boolean(
      (stream.url && typeof stream.url === 'string' && !stream.url.startsWith('magnet:')) ||
      stream.m3u8Url ||
      stream.ytId ||
      stream.embedUrl
    ) && !stream.behaviorHints?.notWebReady;

    let isSupported = true;
    let incompatibleReason: string | undefined;

    if (isTorrent) {
      isSupported = false;
      incompatibleReason = 'Stream P2P / Torrent: requiere un motor de torrents local no disponible en cliente web.';
    } else if (stream.behaviorHints?.notWebReady) {
      isSupported = false;
      incompatibleReason = 'Stream no compatible con reproducción web directa (notWebReady).';
    } else if (!isDirectPlayable) {
      isSupported = false;
      incompatibleReason = 'Stream no compatible con reproducción web directa.';
    }

    return {
      ...stream,
      title,
      headers: stream.headers || stream.behaviorHints?.proxyHeaders?.request,
      isSupported,
      incompatibleReason,
    };
  }

  /**
   * Fetches stream options (M3U8, MP4, HLS, embed, magnet) for a media item.
   * Path: /stream/{type}/{id}.json
   */
  public static async fetchStreams(
    plugin: InstalledPlugin,
    type: string,
    id: string
  ): Promise<PluginStreamResponse> {
    // 1. Static plugin check
    if (plugin?.manifest?.behaviorHints?.staticData && plugin.staticPayload?.streams) {
      const streams = (plugin.staticPayload.streams[id] || []).map(PluginDispatcher.classifyStream);
      return { streams };
    }

    if (!plugin?.url) {
      return { streams: [] };
    }

    // 2. HTTP Plugin query
    const baseUrl = this.getPluginBaseUrl(plugin.url);
    const endpoint = `${baseUrl}/stream/${encodeURIComponent(type)}/${encodeURIComponent(id)}.json`;
    try {
      const res = await this.requestJson(endpoint);
      return {
        streams: Array.isArray(res?.streams)
          ? res.streams.map(PluginDispatcher.classifyStream)
          : [],
      };
    } catch (err) {
      console.warn(`[PluginDispatcher] Failed to fetch streams for ${id} from ${plugin?.manifest?.name || 'plugin'}:`, err);
      return { streams: [] };
    }
  }

  /**
   * Fetches reader pages for a manga/comic chapter.
   * Path: /reader/{type}/{id}/{chapterId}.json
   */
  public static async fetchReader(
    plugin: InstalledPlugin,
    type: string,
    id: string,
    chapterId: string
  ): Promise<PluginReaderResponse> {
    // 1. Static plugin check
    if (plugin.manifest.behaviorHints?.staticData && plugin.staticPayload?.reader) {
      const key = `${id}:${chapterId}`;
      const pages = plugin.staticPayload.reader[key] || [];
      return { pages };
    }

    // 2. HTTP Plugin query
    const baseUrl = this.getPluginBaseUrl(plugin.url);
    const endpoint = `${baseUrl}/reader/${encodeURIComponent(type)}/${encodeURIComponent(id)}/${encodeURIComponent(chapterId)}.json`;
    try {
      const res = await this.requestJson(endpoint);
      return (res as PluginReaderResponse) || { pages: [] };
    } catch (err) {
      console.warn(`[PluginDispatcher] Failed to fetch reader pages for ${id} chapter ${chapterId}:`, err);
      return { pages: [] };
    }
  }

  /**
   * Resolves the base root URL from a manifest URL.
   * E.g. "https://example.com/manifest.json" -> "https://example.com"
   */
  public static getPluginBaseUrl(url: string): string {
    const trimmed = url.trim().replace(/\/manifest\.json$/i, '');
    return trimmed.replace(/\/+$/, '');
  }

  /**
   * Universal JSON network requester:
   * 1. Uses browser fetch() directly if CORS permits.
   * 2. Falls back to ExtensionBridgeClient if extension is connected and standard fetch fails.
   */
  private static async requestJson(url: string): Promise<any> {
    try {
      const res = await fetch(url, {
        signal: AbortSignal.timeout(15000),
        headers: {
          'Accept': 'application/json',
        },
      });
      if (res.ok) {
        return await res.json();
      }
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    } catch (fetchErr) {
      // Fallback: If extension bridge is available, route through bridge client (zero bridge changes required)
      const bridge = ExtensionBridgeClient.getInstance();
      const status = await bridge.ping();
      if (status.connected) {
        const bridgeRes = await bridge.fetch(url, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          responseType: 'json',
        });
        if (bridgeRes.ok && bridgeRes.data) {
          return bridgeRes.data;
        }
      }
      throw fetchErr;
    }
  }
}
